import { yupResolver } from '@hookform/resolvers/yup';
import { FormMode, Marketplaces } from 'common/constants/enums';
import { format } from 'date-fns';
import * as _ from 'lodash';
import { PublisherSelect } from 'modules/pages/components/publisher-select';
import { useState } from 'react';
import { useController, useFieldArray, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import {
  CopyIcon,
  DownloadIcon,
  LogoutIcon,
  PlusOutlinedIcon,
} from '../../../../assets/images/icons';
import {
  CreateTemporaryLinkBody,
  TemporaryLinkService,
} from '../../../../common/services/temporary-link-service.api';
import { Button } from '../../../../common/ui/button';
import { Loader } from '../../../../common/ui/loader';
import { Modal } from '../../../../common/ui/modal';
import { Select } from '../../../../common/ui/select';
import { SingleDatePicker } from '../../../../common/ui/single-date-picker';
import { utilDownloadCsv } from '../../../../common/utils';
import { utilCopyToClipboard } from '../../../../common/utils/utilCopyToClipboard';
import { createTemporaryLinkValidateSchema } from '../../../../common/validationSchemas';
import { LinkEditRow } from './link-edit-row';
import {
  EXPORT_REGION_OPTIONS,
  ExportRegionOption,
  formatExportRegionOptionLabel,
  formatMarketplaceOptionLabel,
  ITagCreationForm,
  MARKETPLACE_LOGOS,
  MARKETPLACE_OPTIONS,
  renderLinkParams,
  resolveLink,
  TAG_CREATION_FORM_DEFAULTS,
} from './temporary-links-form.constants';
import styles from './temporary-links-form.module.scss';

interface TemporaryLinksFormProps {
  onPublisherSelect(publisherId: number, region?: string): void;
  onShowTagsButtonClick(): void;
  onCreateTag(): void;
}

const TemporaryLinksForm = ({
  onCreateTag,
  onPublisherSelect,
  onShowTagsButtonClick,
}: TemporaryLinksFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [csvLoader, setCsvLoader] = useState(false);
  const [fieldIndex, setFieldIndex] = useState<number>(null);
  const [exportRegion, setExportRegion] = useState<ExportRegionOption>(EXPORT_REGION_OPTIONS[0]);
  const [advancedFields, setAdvancedFields] = useState<Set<string>>(new Set());

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
  } = useForm<ITagCreationForm>({
    defaultValues: TAG_CREATION_FORM_DEFAULTS,
    resolver: yupResolver(createTemporaryLinkValidateSchema),
  });

  const { append, fields, remove, replace } = useFieldArray({ control, name: 'tags' });
  const expireDate = useController({ control, name: 'date' });
  const publisherID = useController({ control, name: 'publisherID' });
  const publisherCountry = useController({ control, name: 'publisherCountry' });
  const marketplace = useController({ control, name: 'marketplace' });

  const currentMarketplace = marketplace.field.value || Marketplaces.Amazon;

  const isFieldAdvanced = (fieldId: string) => advancedFields.has(fieldId);

  const toggleAdvanced = (fieldId: string, index: number) => {
    setAdvancedFields((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) {
        next.delete(fieldId);
      } else {
        next.add(fieldId);
      }
      return next;
    });
    setValue(`tags.${index}.tag`, '');
  };

  const resetAdvancedFields = () => setAdvancedFields(new Set());

  const onSubmit = async ({
    date,
    marketplace,
    publisherCountry,
    publisherID,
    tags,
  }: ITagCreationForm) => {
    const payload: CreateTemporaryLinkBody = {
      marketplace,
      region: publisherCountry || 'us',
      expired_at: format(date, 'yyyy-MM-dd'),
      product_links: tags.map(({ itemID, tag }, index) => {
        const isAdvanced = isFieldAdvanced(fields[index]?.id);
        return {
          marketplace_item_id: itemID,
          link: resolveLink(itemID, tag, marketplace, isAdvanced),
        };
      }),
      publisher_id: publisherID || null,
    };

    setIsLoading(true);

    try {
      await TemporaryLinkService.createLink(payload);
      reset({ ...TAG_CREATION_FORM_DEFAULTS, publisherID });
      resetAdvancedFields();
      toast.success('Links created successfully!');
      onCreateTag();
    } catch (err: any) {
      toast.error(err?.message || 'Something went wrong. Try again later');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadCsvFile = async () => {
    setCsvLoader(true);
    try {
      const res = await TemporaryLinkService.downloadCsvFile(exportRegion.value);
      if (res.data) {
        const fileName = res.headers['content-disposition'].match(/filename="(.*)"/)[1];
        utilDownloadCsv(res.data, fileName);
      }
    } catch (err: any) {
      toast.error(err?.message || 'Something went wrong. Try again later');
    } finally {
      setCsvLoader(false);
    }
  };

  const openElemDetails = (index: number) => {
    const tag = getValues(`tags.${index}.tag`);
    const itemID = getValues(`tags.${index}.itemID`);
    const isAdvanced = isFieldAdvanced(fields[index]?.id);

    if (isAdvanced) {
      if (tag) {
        setFieldIndex(index);
      } else {
        toast.error('Type full link');
      }
      return;
    }

    if (tag && itemID) {
      setFieldIndex(index);
    } else if (!itemID && tag) {
      toast.error('Type itemID');
    } else if (!tag && itemID) {
      toast.error('Type tags');
    } else {
      toast.error('Type product itemID and tags');
    }
  };

  const onMarketplaceChange = (option: { value: Marketplaces }) => {
    marketplace.field.onChange(option.value);
    replace([{ itemID: '', tag: '' }]);
    resetAdvancedFields();
  };

  const renderDetailModal = () => {
    if (fieldIndex === null) return null;

    const modalFieldId = fields[fieldIndex]?.id;
    const isAdvanced = modalFieldId ? advancedFields.has(modalFieldId) : false;
    const itemID = getValues(`tags.${fieldIndex}.itemID`);
    const tag = getValues(`tags.${fieldIndex}.tag`);
    const link = resolveLink(itemID, tag, currentMarketplace, isAdvanced);

    return (
      <Modal onClose={() => setFieldIndex(null)} open>
        <Modal.Title>{`Product: ${itemID}`}</Modal.Title>
        <Modal.Body className={styles.modal_body}>
          <h4 className={styles.modal_subtitle}>
            {isAdvanced ? 'Link parameters:' : 'Current tags:'}
          </h4>
          <div className={styles.link_list}>
            {renderLinkParams(tag, isAdvanced)}
          </div>
          <h4 className={styles.modal_subtitle}>Link: </h4>
          <div className={styles.modal_link_wrapper}>
            <img
              alt={`${currentMarketplace}_icon`}
              className={styles.link_logo}
              src={MARKETPLACE_LOGOS[currentMarketplace]}
            />
            <p className={styles.modal_link}>{link}</p>
            <Button
              className="ml-10"
              iconStart={<CopyIcon />}
              onClick={() => utilCopyToClipboard(link)}
              outlined
              size="small"
              variant="secondary"
            />
          </div>
        </Modal.Body>
        <Modal.Actions>
          <Button
            iconStart={<LogoutIcon />}
            onClick={() => setFieldIndex(null)}
            outlined
            variant="secondary"
          >
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    );
  };

  return (
    <>
      {renderDetailModal()}
      <form className={styles.tag_creation_form} onSubmit={handleSubmit(onSubmit)}>
        {isLoading && (
          <div className={styles.loader_container}>
            <div className={styles.background} />
            <Loader className={styles.loader}>Loading...</Loader>
          </div>
        )}

        <div className="d-flex w-100 justify-space-between mb-30">
          <div className="d-flex column-gap-10">
            <div className={styles.input_wrapper}>
              <SingleDatePicker
                buttonProps={{}}
                onChange={(day) => expireDate.field.onChange(day)}
                value={expireDate.field.value}
              >
                Select end date
              </SingleDatePicker>
              {errors?.date?.message && (
                <p className={styles.error_message}>{errors?.date?.message}</p>
              )}
            </div>
            <div className={styles.input_wrapper}>
              <PublisherSelect
                formMode={FormMode.SEARCH}
                onPublisherChange={({ country, id }) => {
                  onPublisherSelect(id, country);
                  publisherID.field.onChange(id);
                  publisherCountry.field.onChange(country);
                }}
              />
            </div>
            <div className={styles.input_wrapper}>
              <Select
                formatOptionLabel={formatMarketplaceOptionLabel}
                isSearchable={false}
                onChange={onMarketplaceChange}
                options={MARKETPLACE_OPTIONS}
                value={MARKETPLACE_OPTIONS.find((o) => o.value === currentMarketplace) || MARKETPLACE_OPTIONS[0]}
              />
            </div>
          </div>
          <div className="d-flex column-gap-20 align-center">
            <Button
              iconStart={<PlusOutlinedIcon />}
              onClick={() => append({ itemID: '', tag: '' })}
              variant="secondary"
            >
              Add new
            </Button>
            <Select
              formatOptionLabel={formatExportRegionOptionLabel}
              isSearchable={false}
              onChange={(option) => setExportRegion(option)}
              options={EXPORT_REGION_OPTIONS}
              value={exportRegion}
            />
            <Button
              iconStart={<DownloadIcon />}
              loading={csvLoader}
              onClick={downloadCsvFile}
              variant="secondary"
            />
          </div>
        </div>

        <div className={styles.tags_container}>
          {fields.map((field, index) => (
            <LinkEditRow
              key={field.id}
              fieldId={field.id}
              getValues={(name) => getValues(name)}
              index={index}
              isAdvanced={isFieldAdvanced(field.id)}
              isDeleteDisabled={fields.length < 2}
              itemIDError={_.get(errors, `tags.${index}.itemID.message`)}
              marketplace={currentMarketplace}
              onDelete={() => remove(index)}
              onShowValues={() => openElemDetails(index)}
              onToggleAdvanced={() => toggleAdvanced(field.id, index)}
              register={register}
              tagError={_.get(errors, `tags.${index}.tag.message`)}
            />
          ))}
        </div>

        <div className={styles.button_container}>
          <Button onClick={() => onShowTagsButtonClick()} type="button" variant="primary">
            Show hardcoded links
          </Button>
          <Button type="submit" variant="secondary">
            Create
          </Button>
        </div>
      </form>
    </>
  );
};

export { TemporaryLinksForm };
